<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  board: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['updated', 'close']);

const store = useStore();
const { t } = useI18n();

const PALETTE = [
  '#64748b',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#f59e0b',
  '#10b981',
  '#f43f5e',
  '#06b6d4',
];

const dialogRef = ref(null);
const columns = ref([]);
const openPaletteFor = ref(null);
const isSaving = ref(false);

const cloneColumns = () =>
  [...(props.board.columns || [])]
    .sort((a, b) => a.position - b.position)
    .map(col => ({
      id: col.id,
      name: col.name,
      color: col.color || '#64748b',
      wip_limit: col.wip_limit || null,
    }));

watch(
  () => props.board,
  () => {
    columns.value = cloneColumns();
  },
  { immediate: true }
);

const canDelete = computed(() => columns.value.length > 1);

const uuid = () => crypto.randomUUID();

const addColumn = () => {
  columns.value.push({
    id: uuid(),
    name: t('TASKS.NEW_COLUMN'),
    color: PALETTE[columns.value.length % PALETTE.length],
    wip_limit: null,
  });
};

const removeColumn = index => {
  if (!canDelete.value) return;
  columns.value.splice(index, 1);
};

const pickColor = (column, hex) => {
  column.color = hex;
  openPaletteFor.value = null;
};

const setWip = (column, value) => {
  const n = parseInt(value, 10);
  column.wip_limit = Number.isFinite(n) && n > 0 ? n : null;
};

const save = async () => {
  if (isSaving.value) return;
  isSaving.value = true;
  const keptIds = columns.value.map(col => col.id);
  const removedIds = (props.board.columns || [])
    .map(col => col.id)
    .filter(id => !keptIds.includes(id));
  const payload = columns.value.map((col, index) => ({
    id: col.id,
    name: (col.name || '').trim() || t('TASKS.NEW_COLUMN'),
    color: col.color,
    wip_limit: col.wip_limit,
    position: index,
  }));
  try {
    const board = await store.dispatch('tasks/updateBoard', {
      id: props.board.id,
      columns: payload,
    });
    emit('updated', { board, removedIds, fallbackColumnId: keptIds[0] });
    dialogRef.value?.close();
  } finally {
    isSaving.value = false;
  }
};

const open = () => dialogRef.value?.open();
const close = () => dialogRef.value?.close();

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="xl"
    :show-confirm-button="false"
    :show-cancel-button="false"
    overflow-y-auto
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('TASKS.PIPELINE_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">{{ t('TASKS.PIPELINE_HINT') }}</p>
      </div>

      <Draggable
        :list="columns"
        item-key="id"
        handle=".col-drag"
        class="flex flex-col gap-2"
        ghost-class="opacity-40"
      >
        <template #item="{ element, index }">
          <div
            class="flex items-center gap-2 p-2 border rounded-lg bg-n-solid-2 border-n-weak"
          >
            <button
              class="col-drag cursor-grab text-n-slate-10 hover:text-n-slate-11"
              :title="t('TASKS.REORDER')"
            >
              <span class="i-lucide-grip-vertical size-4" />
            </button>

            <div class="relative">
              <button
                class="rounded-full size-6 ring-2 ring-white/70 dark:ring-black/30"
                :style="{ backgroundColor: element.color }"
                :title="t('TASKS.COLOR')"
                @click="
                  openPaletteFor =
                    openPaletteFor === element.id ? null : element.id
                "
              />
              <div
                v-if="openPaletteFor === element.id"
                class="absolute z-10 flex flex-wrap gap-1.5 p-2 mt-1 border rounded-lg shadow-lg w-36 bg-n-solid-1 border-n-weak"
              >
                <button
                  v-for="hex in PALETTE"
                  :key="hex"
                  class="rounded-full size-6 hover:scale-110 transition-transform"
                  :style="{ backgroundColor: hex }"
                  @click="pickColor(element, hex)"
                />
              </div>
            </div>

            <input
              v-model="element.name"
              type="text"
              :placeholder="t('TASKS.COLUMN_NAME')"
              class="flex-1 min-w-[7rem] px-2.5 py-1.5 text-sm border rounded-md outline-none bg-n-alpha-black2 text-n-slate-12 border-n-weak focus:border-n-brand"
            />

            <div
              class="flex items-center gap-1.5 shrink-0"
              :title="t('TASKS.WIP_LIMIT_HINT')"
            >
              <span class="text-xs text-n-slate-10">{{
                t('TASKS.LIMIT')
              }}</span>
              <input
                :value="element.wip_limit || ''"
                type="number"
                min="0"
                :placeholder="t('TASKS.NO_LIMIT')"
                class="w-14 px-2 py-1.5 text-sm text-center border rounded-md outline-none bg-n-alpha-black2 text-n-slate-12 border-n-weak focus:border-n-brand"
                @input="setWip(element, $event.target.value)"
              />
            </div>

            <button
              class="p-1 rounded-md text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-ruby-3 disabled:opacity-30 disabled:cursor-not-allowed"
              :disabled="!canDelete"
              :title="t('TASKS.DELETE_COLUMN')"
              @click="removeColumn(index)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </template>
      </Draggable>

      <button
        class="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg text-n-slate-11 border border-dashed border-n-weak hover:border-n-brand hover:text-n-slate-12"
        @click="addColumn"
      >
        <span class="i-lucide-plus size-4" />
        {{ t('TASKS.ADD_COLUMN') }}
      </button>

      <p class="text-xs text-n-slate-10">{{ t('TASKS.DELETE_COLUMN_HINT') }}</p>

      <div
        class="flex items-center justify-end gap-2 pt-2 border-t border-n-weak"
      >
        <button
          class="px-4 py-2 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
          @click="close"
        >
          {{ t('TASKS.CANCEL') }}
        </button>
        <button
          :disabled="isSaving"
          class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:bg-n-brand/90 disabled:opacity-50"
          @click="save"
        >
          {{ t('TASKS.SAVE') }}
        </button>
      </div>
    </div>
  </Dialog>
</template>
